import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@env/environment';
import { Page } from '@interfaces/page';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { CustomerHttpParamsCodec } from '@helpers/custom-http-params-codec';
import { ListFiscalReceipt } from '../_models/list-fiscal-receipt.model';
import { IcashStore } from '../_models/icash-store.model';
import { IcashCashRegister } from '../_models/icash-cash-register.model';
import { FiscalReceiptDetails } from '../_models/fiscal-receipt-details.model';

const fiscalReceiptsURL = environment.apiBaseURL + '/icash-receipt';

const pageableFiscalReceipts = fiscalReceiptsURL + '/pageable';
const storesUrl              = fiscalReceiptsURL + '/stores';
const cashRegistersUrl       = fiscalReceiptsURL + '/cash-registers';

@Injectable({
  providedIn: 'root'
})
export class FiscalReceiptsService {

  constructor(private http: HttpClient) { }

  /**
   * One page of receipts. Unlike the other report services there is no paired count call - the
   * receipts come from iCash, which returns the total in the same response, so totalElements is
   * already on the page.
   */
  public findAllByFilter(sortingPaging: SortingPagingData, searchForm: any, fromDate: string, toDate: string): Observable<Page<ListFiscalReceipt>> {
    return this.http.get<Page<ListFiscalReceipt>>(pageableFiscalReceipts,
      { params: this.createSearchHttpRequestParams(sortingPaging, searchForm, fromDate, toDate) });
  }

  /** One receipt in full, for the detail popup. */
  public findReceipt(operationId: number): Observable<FiscalReceiptDetails> {
    return this.http.get<FiscalReceiptDetails>(`${fiscalReceiptsURL}/${operationId}`);
  }

  public findStores(): Observable<IcashStore[]> {
    return this.http.get<IcashStore[]>(storesUrl);
  }

  /** Every register of the client; the component narrows them by store itself. */
  public findCashRegisters(): Observable<IcashCashRegister[]> {
    return this.http.get<IcashCashRegister[]>(cashRegistersUrl);
  }

  private createSearchHttpRequestParams(sortingPaging: SortingPagingData, searchForm: any, fromDate: string, toDate: string) {
    let requestParams = new HttpParams({encoder: new CustomerHttpParamsCodec()});

    if (sortingPaging != null) {
      requestParams = requestParams.set("page", sortingPaging.getPageNumber());
      requestParams = requestParams.set("pageSize", sortingPaging.getPageSize());
    }

    // Set one by one rather than looping the form: an empty select sends '', which would
    // otherwise reach the backend as a blank storeId and fail to bind to an Integer.
    if (searchForm.storeId) {
      requestParams = requestParams.set("storeId", searchForm.storeId);
    }

    if (searchForm.cashRegisterId) {
      requestParams = requestParams.set("cashRegisterId", searchForm.cashRegisterId);
    }

    if (fromDate && fromDate.length !== 0) {
      requestParams = requestParams.set("dateFrom", fromDate);
    }

    if (toDate && toDate.length !== 0) {
      requestParams = requestParams.set("dateTo", toDate);
    }

    return requestParams;
  }

}
